import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Spinner, Alert, Container, Row, Col } from 'react-bootstrap';
import * as MedicineTagListService from '../lib/MedicineTagListService';
import { MedicineTagListOutcome } from '../lib/MedicineTagListResponse';

export default function MedicineListSelectionPopup({
  show,
  onHide,
  instituteId,
  onLoad,
}) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedTagId, setSelectedTagId] = useState(null);

  useEffect(() => {
    if (show && instituteId) {
      loadTags();
    }
  }, [show, instituteId]);

  const loadTags = async () => {
    setLoading(true);
    setError('');
    setSelectedTagId(null);
    setTags([]);

    try {
      const result = await MedicineTagListService.getByInstitute(instituteId);

      if (result.outcome === MedicineTagListOutcome.SUCCESS) {
        const tagList = Array.isArray(result.data) ? result.data : [];
        // Filter to active tags only (activeInd === 'Y')
        const activeTags = tagList.filter((tag) => tag.activeInd === 'Y');
        setTags(activeTags);
      } else if (result.outcome === MedicineTagListOutcome.BUSINESS_REJECTION) {
        setError(result.message || 'Could not load medicine lists.');
      } else {
        setError(result.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError(`Error fetching medicine lists: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLoad = () => {
    if (selectedTagId !== null && onLoad) {
      const selectedTag = tags.find((tag) => tag.medicineTagId === selectedTagId);
      if (selectedTag) {
        onLoad(selectedTag);
        onHide();
      }
    }
  };

  const getActiveItemCount = (tag) => {
    if (!tag.items) return 0;
    return tag.items.filter((item) => item.activeInd === 'Y').length;
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" scrollable={true}>
      <Modal.Header closeButton>
        <Modal.Title>Load from Medicine List</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Container fluid>
          {loading && (
            <Row>
              <Col lg="12" className="text-center py-4">
                <Spinner animation="border" role="status" />
                <div className="mt-2">Loading medicine lists…</div>
              </Col>
            </Row>
          )}

          {!loading && error && (
            <Row>
              <Col lg="12" className="p-0">
                <Alert variant="danger" className="mb-0">
                  {error}
                </Alert>
              </Col>
            </Row>
          )}

          {!loading && !error && tags.length === 0 && (
            <Row>
              <Col lg="12" className="p-0">
                <Alert variant="info" className="mb-0">
                  No medicine lists found for this institute.
                </Alert>
              </Col>
            </Row>
          )}

          {!loading && !error && tags.length > 0 && (
            <Row>
              <Col lg="12" className="p-0">
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {tags.map((tag) => (
                    <div key={tag.medicineTagId} className="mb-2">
                      <Form.Check
                        type="radio"
                        name="medicine-list-pick"
                        id={`medicine-list-pick-${tag.medicineTagId}`}
                        label={`${tag.tagName} (${getActiveItemCount(tag)} medicine${
                          getActiveItemCount(tag) !== 1 ? 's' : ''
                        })`}
                        value={tag.medicineTagId}
                        checked={Number(selectedTagId) === Number(tag.medicineTagId)}
                        onChange={() => setSelectedTagId(tag.medicineTagId)}
                      />
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          )}
        </Container>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleLoad}
          disabled={selectedTagId === null || loading}
        >
          Load
        </Button>
        <Button variant="outline-secondary" onClick={onHide}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
